import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TablePagination } from '../../../components/common/TablePagination';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { useCasePermissions } from '../../../hooks/UseCasePermission';
import { ploTableFilters } from '../../../hooks/plo/PLOTableFilter';
import { deletePloForm, fetchPloApplications } from '../../../services/PloThreeService';
import type { User } from '../../../types/user';
import { formatDateToMyanmar } from '../../../utils/GlobalUtils';

export function PLOFormThreeTable() {
  const navigate = useNavigate();

  const itemsPerPage = 10;
  const filters = ploTableFilters();

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  useCasePermissions(currentUser);

  // Helper to verify if current user is the origin creator or belongs to the origin office
  const checkIsOriginUser = (app: any): boolean => {
    if (!currentUser) return false;

    // 1. Match by Office ID (Top-level originOfficeId or nested originOffice.id / officeId)
    const originOfficeId = app.originOfficeId || app.originOffice?.id || app.document?.originOfficeId || app.officeId;
    if (currentUser.officeId && originOfficeId && currentUser.officeId === originOfficeId) {
      return true;
    }

    // 2. Match by Creator User ID
    const createdById = app.createdById || app.createdBy?.id;
    if (currentUser.id && createdById && currentUser.id === createdById) {
      return true;
    }

    return false;
  };

  const { data, isLoading, isError, error, isPlaceholderData, refetch, isFetching } = useQuery({
    queryKey: [
      'ploFormThree',
      filters.debouncedSearch,
      filters.debouncedCaseYear,
      filters.debouncedParty,
      filters.adviceGivenDate,
      filters.prosecutedStartDate,
      filters.prosecutedEndDate,
      filters.currentPage,
    ],
    queryFn: () =>
      fetchPloApplications({
        searchTerm: filters.debouncedSearch,
        caseYear: filters.debouncedCaseYear,
        partySearch: filters.debouncedParty,
        adviceGivenDate: filters.adviceGivenDate,
        prosecutedStartDate: filters.prosecutedStartDate,
        prosecutedEndDate: filters.prosecutedEndDate,
        currentPage: filters.currentPage,
        itemsPerPage,
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const rawList = Array.isArray(data) ? data : data?.data;
  const applications = Array.isArray(rawList) ? rawList : [];
  const totalItems = data?.totalItems || applications.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleDelete = async (id?: string) => {
    if (!id) return;

    const confirmed = window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။');
    if (!confirmed) return;

    try {
      await deletePloForm(id);
      await refetch();
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err?.message || 'ဖျက်ခြင်း မအောင်မြင်ပါ။');
    }
  };

  return (
    <div className="space-y-4 bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            PLOပုံစံ (၃) စာရင်းများ
          </h2>
        </div>

        {/* Action Buttons Group */}
        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန်"
          >
            <svg
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            နောက်ဆုံး Data များ ရယူရန်
          </button>

          <button
            type="button"
            onClick={() => navigate('/plo-form-three/create')}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            ဖောင်အသစ်ထည့်ရန်
          </button>
        </div>
      </div>

      {/* Filter Controls Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 items-end bg-slate-50/60 p-3 rounded-xl border border-slate-100">
        {/* 1. General Search */}
        <div className="lg:col-span-1">
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အထွေထွေ ရှာဖွေရန်</label>
          <input
            type="text"
            placeholder="စာရွက်စာတမ်းနံပါတ်/အမှုနံပါတ်/ပုဒ်မ..."
            value={filters.searchTerm}
            onChange={(e) => {
              filters.setSearchTerm(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* 2. Case Year Search */}
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အမှုခုနှစ်</label>
          <input
            type="number"
            placeholder="၂၀၂၄..."
            value={filters.caseYear}
            onChange={(e) => {
              filters.setCaseYear(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* 3. Plaintiff / Defendant Search */}
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">တရားလို/တရားခံ</label>
          <input
            type="text"
            placeholder="တရားလို/တရားခံ အမည်..."
            value={filters.partySearch}
            onChange={(e) => {
              filters.setPartySearch(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* 4. Advice Given Date */}
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အကြံပေးချသည့်နေ့စွဲ</label>
          <FormDatePicker
            value={filters.adviceGivenDate}
            onChange={(e) => {
              filters.setAdviceGivenDate(e.target.value);
              filters.setCurrentPage(1);
            }}
            name="adviceGivenDate"
            className="w-full"
          />
        </div>

        {/* 5. Prosecuted Date Range (Start & End Date) */}
        <div className="lg:col-span-2 flex items-center gap-1.5">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">တရားစွဲ တင်သည့်နေ့စွဲ (မှ)</label>
            <FormDatePicker
              value={filters.prosecutedStartDate}
              onChange={(e) => {
                filters.setProsecutedStartDate(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="prosecutedStartDate"
              className="w-full"
            />
          </div>

          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">တရားစွဲ တင်သည့်နေ့စွဲ (ထိ)</label>
            <FormDatePicker
              value={filters.prosecutedEndDate}
              onChange={(e) => {
                filters.setProsecutedEndDate(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="prosecutedEndDate"
              className="w-full"
            />
          </div>

          {filters.hasActiveFilters && (
            <button
              type="button"
              onClick={filters.handleClearFilters}
              title="စစ်ထုတ်မှုများ ဖျက်ရန်"
              className="mt-5 shrink-0 inline-flex items-center justify-center text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-md py-1.5 px-2 bg-white transition-colors"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Table Section (BLACK BORDERS & TEXT ONLY) */}
      <div className="overflow-hidden border border-black bg-white shadow-sm relative">
        {isPlaceholderData && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs bg-black text-white px-2.5 py-1 rounded-full shadow-sm">
              နောက်ဆုံးပေါ်အချက်အလက် ရယူနေပါသည်...
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs min-w-[1400px]">
            <thead>
              {/* Top Group Header */}
              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold text-black text-xs">
                <th rowSpan={2} className="border-r border-black p-1.5 w-10">စဉ်</th>
                <th colSpan={2} className="border-r border-black p-1.5 bg-slate-100">ဥပဒေရုံးအမှုအမှတ်</th>
                <th colSpan={8} className="border-r border-black p-1.5 bg-slate-200">အမှုအချက်အလက်</th>
                <th colSpan={6} className="border-r border-black p-1.5 bg-slate-100">အမှုသည် အချက်အလက်</th>
                <th rowSpan={2} className="p-1.5 w-24">လုပ်ဆောင်ချက်</th>
              </tr>

              {/* Sub Columns */}
              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold text-black text-[11px]">
                <th className="border-r border-black p-1.5 w-16">အမှုခုနှစ်</th>
                <th className="border-r border-black p-1.5 w-20">အမှုနံပါတ်</th>
                <th className="border-r border-black p-1.5 w-24">
                  အမှုအမျိုးအစား<br />
                  <span className="text-[9px] font-normal">၁။ရာကြီး၊ ၂။ရာထွေ</span>
                </th>
                <th className="border-r border-black p-1.5 w-28">
                  အမှုအမျိုးအမည်<br />
                  <span className="text-[9px] font-normal">၁။လူသတ်မှု၊ ၂။မုဒိမ်းမှု၊ ၃။သက်ငယ်မုဒိမ်းမှု ၄။ခိုးမှု ၅။လိမ်လည်မှု ၆။လူကုန်ကူးမှု ၇။အခြား</span>
                </th>
                <th className="border-r border-black p-1.5 w-20">ဥပဒေ/ပုဒ်မ</th>
                <th className="border-r border-black p-1.5 w-24">အကြံပေးတောင်းခံသည့်ရက်စွဲ</th>
                <th className="border-r border-black p-1.5 w-24">Turned down ချသည့်နေ့စွဲ</th>
                <th className="border-r border-black p-1.5 w-24">အကြံပေးချသည့်နေ့စွဲ</th>
                <th className="border-r border-black p-1.5 w-24">တရားစွဲ တင်သည့်နေ့စွဲ</th>
                <th className="border-r border-black p-1.5 w-24">တရားရုံးအမှုအမှတ်</th>
                <th className="border-r border-black p-1.5 w-24">တရားလိုအမည်</th>
                <th className="border-r border-black p-1.5 w-24">တရားခံအမည်</th>
                <th className="border-r border-black p-1.5 w-24">တရားခံ ဖမ်းဆီးသည့်နေ့</th>
                <th className="border-r border-black p-1.5 w-24">၁ ရမန် ၂ အာမခံ</th>
                <th className="border-r border-black p-1.5 w-20">ရမန် ကာလ</th>
                <th className="border-r border-black p-1.5 w-24">၁နစ်နာသူ ၂သက်သေ</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-black bg-white">
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td colSpan={18} className="text-center py-12 text-xs text-black font-semibold">
                    အချက်အလက်များ ဆွဲယူနေပါသည်...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={18} className="text-center py-12 text-xs text-red-600 font-semibold">
                    {error instanceof Error ? error.message : 'အချက်အလက်များ ဆွဲယူရာတွင် အမှားအယွင်းရှိခဲ့ပါသည်။'}
                  </td>
                </tr>
              ) : applications.length > 0 ? (
                applications.map((app: any, index: number) => {
                  const formDetails = app.ploFormThree || app;
                  const recordId = app.id || app._id || formDetails.id || formDetails._id;
                  const isOriginUser = checkIsOriginUser(app);

                  const formatDate = (dateVal?: string) =>
                    formatDateToMyanmar(dateVal) || '-';

                  return (
                    <tr key={recordId || index} className="hover:bg-slate-50 transition-colors text-center text-black">
                      {/* Row Index */}
                      <td className="border-r border-black p-1.5 font-bold">{index + 1}</td>

                      {/* ဥပဒေရုံးအမှုအမှတ် */}
                      <td className="border-r border-black p-1.5">{formDetails.caseYear || '-'}</td>
                      <td className="border-r border-black p-1.5 font-bold">{formDetails.caseNumber || '-'}</td>

                      {/* အမှုအချက်အလက် */}
                      <td className="border-r border-black p-1.5">{formDetails.caseType || '-'}</td>
                      <td className="border-r border-black p-1.5">{formDetails.offenseCategory || '-'}</td>
                      <td className="border-r border-black p-1.5 font-semibold">{formDetails.lawSection || formDetails.chargeSection || '-'}</td>
                      <td className="border-r border-black p-1.5">{formatDate(formDetails.adviceRequestedDate)}</td>
                      <td className="border-r border-black p-1.5">{formatDate(formDetails.turnedDownDate)}</td>
                      <td className="border-r border-black p-1.5">{formatDate(formDetails.adviceGivenDate)}</td>
                      <td className="border-r border-black p-1.5">{formatDate(formDetails.prosecutedDate)}</td>
                      <td className="border-r border-black p-1.5">{formDetails.courtCaseNumber || formDetails.courtName || '-'}</td>

                      {/* အမှုသည် အချက်အလက် */}
                      <td className="border-r border-black p-1.5">{formDetails.plaintiffName || formDetails.plaintiffOrAccused || '-'}</td>
                      <td className="border-r border-black p-1.5">{formDetails.defendantName || '-'}</td>
                      <td className="border-r border-black p-1.5">{formDetails.defendantStatus || '-'}</td>
                      <td className="border-r border-black p-1.5">{formDetails.remandStatus || '-'}</td>
                      <td className="border-r border-black p-1.5">{formDetails.trialPeriod || '-'}</td>
                      <td className="border-r border-black p-1.5">{formDetails.residentArea || '-'}</td>

                      {/* Actions Column */}
                      <td className="p-1.5 text-center">
                        {isOriginUser ? (
                          <div className="flex justify-center items-center gap-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/plo-form-three/edit/${app.documentNumber}`)}
                              className="inline-flex items-center justify-center gap-0.5 rounded border border-blue-600 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                              title="ပြင်ဆင်ရန်"
                            >
                              ✏️ ပြင်ရန်
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(app.documentNumber)}
                              className="inline-flex items-center justify-center rounded border border-rose-600 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                              title="ဖျက်ရန်"
                            >
                              🗑️
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400 font-medium">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={18} className="text-center py-12 text-xs text-black font-semibold">
                    ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Pagination */}
        {!isLoading && totalPages > 1 && (
          <TablePagination
            currentPage={filters.currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={filters.setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}